import React, { useMemo, useState } from 'react'
import { NameDto } from '@/api/Common/Types'
import {
  FormCheckboxField,
  FormField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatLargeNumber, formatNumberWithCommas } from '@/utils/formatNumber'
import {
  AdjustNotionalValue,
  IntroducingBrokerDetailsDto,
  IntroducingBrokerRestrictions,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { Minus, Plus } from 'lucide-react'
import { FormSubmitValues } from 'utils/formValidation'

const IntroducingBrokerLoyaltyProgramFormUI = (
  props: FormikProps<AdjustNotionalValue> & OuterProps
) => {
  const [pointState, setPointState] = useState<'add' | 'deduct'>()
  const {
    setFieldValue,
    values,
    introducingBroker,
    bonusNvAdjustmentReasons,
    isSubmitting,
    errors,
    isValid,
  } = props
  const restrictLoyaltyChoice = !!(introducingBroker.restrictions || []).find(
    (x) => x.id === IntroducingBrokerRestrictions.RESTRICT_LOYALTY_CHOICE
  )
  const nextTier = introducingBroker.loyaltyDetails?.nextTier
  const nextTierValue = `${nextTier?.name ?? ''} at ${formatLargeNumber(
    nextTier?.minNotionalValue ?? 0
  )} Notional Value`

  const onCancel = () => {
    props.resetForm()
    props.onCancel()
  }

  const onSubmit = (event?: React.SyntheticEvent) => {
    event?.preventDefault()
    props.onSubmit({
      ...values,
      bonusNvAdjustment:
        pointState === 'deduct' ? -values.bonusNvAdjustment : values.bonusNvAdjustment,
    })
  }

  const getTotalBonusNv = useMemo(() => {
    if (pointState) {
      return (
        (introducingBroker?.loyaltyDetails?.bonusNv ?? 0) +
        (pointState === 'deduct' ? -values.bonusNvAdjustment : values.bonusNvAdjustment)
      )
    }
    return introducingBroker?.loyaltyDetails?.bonusNv
  }, [values.bonusNvAdjustment, introducingBroker.loyaltyDetails?.bonusNv, pointState])

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent size='2xl' render={<Form onSubmit={onSubmit} />}>
        <DialogHeader>
          <DialogTitle>IB Bonus Program</DialogTitle>
        </DialogHeader>

        <FormCheckboxField
          name='loyaltyProgramEnabled'
          label='Loyalty Enabled'
          disabled
          value={introducingBroker.loyaltyProgramEnabled}
          dataTest='is-loyaltyProgramEnabled-field'
        />
        <FormCheckboxField
          name='restrictLoyaltyChoice'
          label='Restrict Loyalty Choice'
          disabled
          value={restrictLoyaltyChoice}
        />
        <FormReadonlyField
          name='totalBonusNv'
          label='Notional Value'
          value={formatNumberWithCommas(getTotalBonusNv ?? 0, { minDecimalPlaces: 0 })}
        />

        <div className='grid gap-4 md:grid-cols-[auto_1fr_1fr] md:items-end'>
          <ButtonGroup className='md:pb-0.5'>
            <Button
              type='button'
              variant={pointState === 'add' ? 'default' : 'outline'}
              size='icon-sm'
              onClick={() => setPointState('add')}
              data-test='increase-reason-input-button'
            >
              <Plus />
            </Button>
            <Button
              type='button'
              variant={pointState === 'deduct' ? 'default' : 'outline'}
              size='icon-sm'
              onClick={() => setPointState('deduct')}
              data-test='decrease-reason-input-button'
            >
              <Minus />
            </Button>
          </ButtonGroup>
          <FormSelectField
            name='reasonId'
            label='Reason'
            options={bonusNvAdjustmentReasons}
            value={values.reasonId || undefined}
            allowClear
            showSearch
            error={errors.reasonId}
            dataTest='ib-reason-field'
            onChange={(value) => setFieldValue('reasonId', value ? Number(value) : 0)}
          />
          <FormField
            name='bonusNvAdjustment'
            label='Adjustment'
            disabled={!pointState}
            min={0.1}
            step={0.1}
            type='number'
            value={values.bonusNvAdjustment}
            onChange={(e) => {
              const { value } = e.target
              setFieldValue('bonusNvAdjustment', value === '' ? 0 : Number(+value))
            }}
          />
        </div>

        {values.reasonId === 3 && (
          <FormField name='reasonText' label='Reason' error={errors.reasonText} />
        )}

        <FormReadonlyField
          name='currentTier'
          label='Current Tier'
          value={introducingBroker.loyaltyDetails?.currentTier?.name ?? ''}
        />
        <FormReadonlyField name='nextTier' label='Next Tier' value={nextTierValue} />

        <DialogFooter>
          <Button disabled={isSubmitting} key='cancel-button' type='button' onClick={onCancel}>
            Cancel
          </Button>
          <Button key='submit-button' type='button' disabled={!isValid} onClick={onSubmit}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface OuterProps {
  isOpen: boolean
  introducingBroker: IntroducingBrokerDetailsDto
  bonusNvAdjustmentReasons: NameDto[]

  onSubmit(values: FormSubmitValues<AdjustNotionalValue>): Promise<void>
  onCancel(): void
}

export const IntroducingBrokerLoyaltyProgramForm = withFormik<OuterProps, AdjustNotionalValue>({
  mapPropsToValues: (props) => {
    return {
      clientId: props.introducingBroker.client.id,
      reasonId: 0,
      reasonText: '',
      bonusNvAdjustment: 0,
    }
  },
  handleSubmit: async (values, { props }) => {
    await props.onSubmit(values)
  },
  validate: (values) => {
    const errors: FormikErrors<AdjustNotionalValue> = {}
    if (!values.reasonId) {
      errors.reasonId = 'Required'
    }
    if (values.reasonId === 3 && !values.reasonText) {
      errors.reasonText = 'Required'
    }
    return errors
  },
  isInitialValid: false,
  validateOnMount: true,
})(IntroducingBrokerLoyaltyProgramFormUI)
